import { Fragment, useState, type JSX, type MouseEvent } from 'react';

import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';

import { APP_VERSION } from '@/constants/config';
import { NAV_GROUPS, visibleGroupItems, type NavItem } from '@/constants/nav';
import { selectUser, selectUserRoles } from '@/store/userSlice';
import { loggedOut } from '@/store/index';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { RoleChips } from '@/components/RoleChips';
import { Brand } from './Brand';

// Top app-bar = the SOLE nav chrome (ADR-0035 — reverses the ADR-0029 §5
// sidebar shell; palette/typography/chip tokens are retained). 56px, surface
// bg, bottom border. Toolbar order: BRAMBO lockup (ONCE, left) → role-filtered
// nav-button cluster (groups flattened, vertical divider between groups) →
// flex spacer → user chip (avatar + name + RoleChips + logout) → muted version
// text (hidden <md). On <md the nav cluster collapses to a hamburger Menu.
// The Vapas global filter / search / notification bell stay OMITTED (ADR-0029).
const AVATAR_SIZE = 28;
const USER_CHIP_HEIGHT = 36;
const PILL_RADIUS = 999;

// Two-letter initials (e.g. "Markéta Janíková" → "MJ"). Falls back to the
// email's first letter if the names are missing.
const initials = (first: string, last: string, email: string): string => {
  const a = (first[0] ?? '').toUpperCase();
  const b = (last[0] ?? '').toUpperCase();
  if (a || b) return `${a}${b}`;
  return (email[0] ?? '?').toUpperCase();
};

// Active-item tokens — reused verbatim from the deleted SidebarItem so the
// top-nav highlight matches the prior shell exactly: primary.light bg +
// primary.main color + weight 600 + aria-current="page".
const isActive = (pathname: string, to: string): boolean => pathname.startsWith(to);

export const Topbar = (): JSX.Element => {
  const { t } = useTranslation();
  const auth = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const userRoles = useAppSelector(selectUserRoles);
  // `md` breakpoint (900px) — desktop inline nav row vs. mobile hamburger.
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const openMenu = (e: MouseEvent<HTMLElement>): void => setMenuAnchor(e.currentTarget);
  const closeMenu = (): void => setMenuAnchor(null);

  const handleLogout = (): void => {
    // Wipe the store BEFORE handing off to Keycloak. `oidc-client-ts` removes
    // the user and then navigates, resolving its promise from a `pageshow`
    // listener — so a Back press after logout restores this page with the store
    // still holding user A's profile (enough to paint their name, e-mail and
    // roles back on screen) plus their jízdy, drivers and reports. Clearing
    // first makes that impossible.
    dispatch(loggedOut());
    void auth.signoutRedirect();
  };

  const go = (to: string): void => {
    closeMenu();
    navigate(to);
  };

  // Role-filtered groups (same call the Sidebar used); empty groups dropped.
  const groups = NAV_GROUPS.map((group) => ({
    labelKey: group.labelKey,
    items: visibleGroupItems(group, userRoles),
  })).filter((group) => group.items.length > 0);

  const navButton = (item: NavItem): JSX.Element => {
    const active = isActive(location.pathname, item.to);
    const Icon = item.icon;
    return (
      <Button
        key={item.to}
        onClick={() => navigate(item.to)}
        startIcon={<Icon sx={{ fontSize: 18 }} />}
        aria-current={active ? 'page' : undefined}
        sx={(th) => ({
          minHeight: 44,
          px: 1.5,
          textTransform: 'none',
          fontSize: 13.5,
          borderRadius: 1, // 8px
          bgcolor: active ? 'primary.light' : 'transparent',
          color: active ? th.palette.primary.main : th.palette.text.secondary,
          fontWeight: active ? 600 : 400,
          '&:hover': {
            bgcolor: active ? th.palette.primary.light : th.palette.action.hover,
            color: active ? th.palette.primary.main : th.palette.text.primary,
          },
        })}
      >
        {t(item.labelKey)}
      </Button>
    );
  };

  return (
    <AppBar position="sticky">
      <Toolbar
        disableGutters
        sx={(th) => ({
          minHeight: 56,
          height: 56,
          px: th.spacing(3),
          gap: th.spacing(2),
          display: 'flex',
          alignItems: 'center',
        })}
      >
        {/* Brand lockup — left, rendered ONCE */}
        <Brand />

        {/* Nav cluster — desktop inline row, or mobile hamburger Menu */}
        {isCompact ? (
          <>
            <IconButton
              onClick={openMenu}
              aria-label={t('topbar.menu')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              sx={{ color: 'text.secondary' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={closeMenu}
              MenuListProps={{ 'aria-label': t('topbar.nav') }}
            >
              {groups.flatMap((group, gi) => {
                const items = group.items.map((item) => {
                  const active = isActive(location.pathname, item.to);
                  const Icon = item.icon;
                  return (
                    <MenuItem
                      key={item.to}
                      onClick={() => go(item.to)}
                      selected={active}
                      aria-current={active ? 'page' : undefined}
                      sx={{ minHeight: 44 }}
                    >
                      <ListItemIcon>
                        <Icon sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{
                          sx: { fontWeight: active ? 600 : 400 },
                        }}
                      >
                        {t(item.labelKey)}
                      </ListItemText>
                    </MenuItem>
                  );
                });
                return gi > 0
                  ? [<Divider key={`div-${group.labelKey}`} />, ...items]
                  : items;
              })}
            </Menu>
          </>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center" component="nav" aria-label={t('topbar.nav')}>
            {groups.map((group, gi) => (
              <Fragment key={group.labelKey}>
                {gi > 0 ? (
                  <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.25 }} />
                ) : null}
                {group.items.map((item) => navButton(item))}
              </Fragment>
            ))}
          </Stack>
        )}

        {/* Spacer pushes the user-chip cluster to the right */}
        <Box sx={{ flex: 1 }} />

        {/* User-chip cluster — avatar (primary green per §6.7) + name +
            RoleChips, with logout inside the same bordered pill. */}
        {user ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={(th) => ({
              border: `1px solid ${th.palette.divider}`,
              borderRadius: PILL_RADIUS,
              bgcolor: 'background.paper',
              pl: 1,
              pr: 0.5,
              py: 0.25,
              minHeight: USER_CHIP_HEIGHT,
            })}
          >
            <Avatar
              sx={(th) => ({
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                // DESIGN.md §6.7 — user/manager avatar = primary green.
                bgcolor: th.palette.primary.main,
                color: th.palette.primary.contrastText,
                fontSize: 11,
                fontWeight: 600,
              })}
            >
              {initials(user.firstName, user.lastName, user.email)}
            </Avatar>
            {!isCompact ? (
              <Stack sx={{ lineHeight: 1.1 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.primary', fontSize: 12 }}
                >
                  {user.firstName} {user.lastName}
                </Typography>
                <Box sx={{ mt: 0.25 }}>
                  <RoleChips roles={userRoles} />
                </Box>
              </Stack>
            ) : null}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
            <Button
              onClick={handleLogout}
              aria-label={t('topbar.logoutAria')}
              variant="text"
              disableRipple
              sx={(th) => ({
                minHeight: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: 13,
                borderRadius: PILL_RADIUS,
                color: th.palette.text.secondary,
                '&:hover': {
                  color: th.palette.primary.dark,
                  bgcolor: th.palette.primary.light,
                },
              })}
            >
              {t('topbar.logout')}
            </Button>
          </Stack>
        ) : null}

        {/* Muted version text — hidden <md (mockup footer slot moved here) */}
        {!isCompact ? (
          <Typography
            component="span"
            sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            {t('topbar.version', { version: APP_VERSION })}
          </Typography>
        ) : null}
      </Toolbar>
    </AppBar>
  );
};
